import React from "react";
import {
  useThemeMode,
  systemMode,
} from "../../../_metronic/partials/layout/theme-mode/ThemeModeProvider";
import {genericGetAll} from '../generic/crud'
import {ENTITY_ID_WATER_WASH} from '../generic/Constants'

type HeatmapEntry = {
  date: string; // "2026-02-22"
  jobsClosed: number;
  jobsCreated: number;
  waterWash: number;
};

type ActivityHeatMapCardProps = {
  // New: pre-computed heatmap (Admin Dashboard)
  heatmap?: HeatmapEntry[];
  // Old: raw data arrays (Manager/Supervisor/Finance)
  allClosedJobs?: any[];
  allActiveJobs?: any[];
  loading?: boolean;
  className?: string;
};

export const ActivityHeatMapCard: React.FC<ActivityHeatMapCardProps> = ({
  heatmap,
  allClosedJobs,
  allActiveJobs,
  loading,
  className
}) => {
  const { mode } = useThemeMode();
  const isDarkMode =
    mode === "dark" || (mode === "system" && systemMode === "dark");
  const [selectedDay, setSelectedDay] = React.useState<{
    date: Date;
    jobsClosed: number;
    jobsCreated: number;
    waterWash: number;
  } | null>(null);
  const [waterWashData, setWaterWashData] = React.useState<any[]>([]);

  // Self-fetch water wash data when in old mode
  const isPreComputed = heatmap !== undefined;
  React.useEffect(() => {
    if (isPreComputed) return;
    genericGetAll(ENTITY_ID_WATER_WASH).then((res: any) => {
      setWaterWashData(res?.data?.data || []);
    }).catch(() => {});
  }, [isPreComputed]);

  // Convert heatmap entries to internal format with Date objects
  const heatmapData = React.useMemo(() => {
    if (isPreComputed) {
      if (!heatmap || heatmap.length === 0) return [];
      return heatmap.map(entry => {
        const date = new Date(entry.date + 'T00:00:00');
        return {
          date,
          count: entry.jobsClosed,
          jobsCreated: entry.jobsCreated,
          waterWashCount: entry.waterWash
        };
      });
    }

    // Build heatmap from raw data (old mode)
    const now = new Date();
    const daysToShow = 56; // 8 weeks
    const dayMap = new Map<string, { count: number; jobsCreated: number; waterWashCount: number }>();

    for (let i = 0; i < daysToShow; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      dayMap.set(key, { count: 0, jobsCreated: 0, waterWashCount: 0 });
    }

    for (const job of (allClosedJobs || [])) {
      if (job.endDate) {
        const key = new Date(job.endDate).toISOString().slice(0, 10);
        const entry = dayMap.get(key);
        if (entry) entry.count++;
      }
    }
    for (const job of [...(allClosedJobs || []), ...(allActiveJobs || [])]) {
      if (job.createdOn) {
        const key = new Date(job.createdOn).toISOString().slice(0, 10);
        const entry = dayMap.get(key);
        if (entry) entry.jobsCreated++;
      }
    }
    for (const ww of waterWashData) {
      if (ww.createdOn) {
        const key = new Date(ww.createdOn).toISOString().slice(0, 10);
        const entry = dayMap.get(key);
        if (entry) entry.waterWashCount++;
      }
    }

    return Array.from(dayMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateStr, data]) => ({
        date: new Date(dateStr + 'T00:00:00'),
        count: data.count,
        jobsCreated: data.jobsCreated,
        waterWashCount: data.waterWashCount
      }));
  }, [heatmap, isPreComputed, allClosedJobs, allActiveJobs, waterWashData]);

  // Heatmap Calendar Component - shows current month days
  const HeatmapCalendar = () => {
    const maxCount = Math.max(...heatmapData.map((d) => d.count), 1);
    const daysWithJobs = heatmapData.filter((d) => d.count > 0).length;
    const averageCount =
      daysWithJobs > 0
        ? heatmapData.reduce((sum, d) => sum + d.count, 0) / daysWithJobs
        : 0;

    const getIntensity = (count: number) => {
      if (count === 0) return 0;
      const ratio = count / maxCount;
      if (ratio <= 0.25) return 1;
      if (ratio <= 0.5) return 2;
      if (ratio <= 0.75) return 3;
      return 4;
    };

    const getColor = (count: number, intensity: number, waterWashCount: number = 0) => {
      // If no jobs but has water wash, return orange
      if (count === 0 && waterWashCount > 0) {
        return isDarkMode ? "#ff8c42" : "#ffa726";
      }

      if (intensity === 0) return isDarkMode ? "#1e1e2d" : "#f0f0f0";

      // Above average: Blue shades
      if (count > averageCount) {
        const blueColors = isDarkMode
          ? ["#1e3a5f", "#2d5a8f", "#3d7ac7", "#009ef7"]
          : ["#cfe2ff", "#9ec5fe", "#6ea8fe", "#009ef7"];
        return blueColors[intensity - 1];
      }

      // At or below average: Green shades
      const greenColors = isDarkMode
        ? ["#1a3a2e", "#2d5f4c", "#40856a", "#50cd89"]
        : ["#d4f4e0", "#a8e6c8", "#7dd9a8", "#50cd89"];
      return greenColors[intensity - 1];
    };

    // For current month calendar layout
    // Get the first day of month to determine starting weekday
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const startWeekday = firstDayOfMonth.getDay(); // 0=Sun, 6=Sat
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Build a map of date -> heatmapData entry for quick lookup
    const dateMap = new Map<string, typeof heatmapData[0]>();
    heatmapData.forEach(entry => {
      const key = `${entry.date.getFullYear()}-${String(entry.date.getMonth() + 1).padStart(2, '0')}-${String(entry.date.getDate()).padStart(2, '0')}`;
      dateMap.set(key, entry);
    });

    // Build calendar grid: 7 rows (days of week), variable columns (weeks)
    const totalCells = startWeekday + daysInMonth;
    const numWeeks = Math.ceil(totalCells / 7);
    const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    // Create grid[row][col] where row=day-of-week, col=week
    const grid: Array<Array<{ dayNum: number; entry: typeof heatmapData[0] | null } | null>> = Array.from(
      { length: 7 },
      () => Array(numWeeks).fill(null)
    );

    for (let day = 1; day <= daysInMonth; day++) {
      const cellIndex = startWeekday + day - 1;
      const weekCol = Math.floor(cellIndex / 7);
      const weekdayRow = cellIndex % 7;
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      grid[weekdayRow][weekCol] = {
        dayNum: day,
        entry: dateMap.get(dateKey) || null
      };
    }

    return (
      <div>
        {/* Heatmap grid with day labels */}
        <div className="d-flex flex-column gap-1">
          {grid.map((row, rowIdx) => (
            <div key={rowIdx} className="d-flex gap-1 align-items-center">
              {/* Day label */}
              <div
                className={`text-center fw-semibold ${
                  isDarkMode ? "text-white" : "text-muted"
                }`}
                style={{
                  width: "30px",
                  fontSize: "10px",
                  opacity: 0.7,
                  marginRight: "6px",
                }}
              >
                {dayLabels[rowIdx]}
              </div>

              {/* Week cells */}
              <div className="d-flex gap-1 flex-grow-1">
                {row.map((cell, colIdx) => {
                  if (!cell) {
                    return (
                      <div
                        key={colIdx}
                        style={{ flex: 1, minWidth: "24px", height: "20px" }}
                      />
                    );
                  }
                  const count = cell.entry?.count || 0;
                  const waterWashCount = cell.entry?.waterWashCount || 0;
                  const intensity = getIntensity(count);
                  const isAboveAverage = count > averageCount;
                  const isWaterWashOnly = count === 0 && waterWashCount > 0;
                  const cellDate = new Date(year, month, cell.dayNum);
                  const isToday = cell.dayNum === now.getDate();
                  return (
                    <div
                      key={colIdx}
                      className="rounded d-flex align-items-center justify-content-center"
                      style={{
                        flex: 1,
                        minWidth: "24px",
                        height: "20px",
                        backgroundColor: getColor(count, intensity, waterWashCount),
                        border: isToday
                          ? `2px solid ${isDarkMode ? "#6ea8fe" : "#009ef7"}`
                          : `1.5px solid ${isDarkMode ? "#2d2d3f" : "#e4e6ef"}`,
                        boxShadow: isToday
                          ? `0 0 6px ${isDarkMode ? "rgba(110,168,254,0.4)" : "rgba(0,158,247,0.4)"}`
                          : "none",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        position: "relative",
                      }}
                      title={`${cellDate.toLocaleDateString()}: ${
                        isWaterWashOnly
                          ? `${waterWashCount} water wash (no jobs)`
                          : `${count} jobs${isAboveAverage ? " (Above avg)" : ""}`
                      }`}
                      onClick={() =>
                        setSelectedDay({
                          date: cellDate,
                          jobsClosed: count,
                          jobsCreated: cell.entry?.jobsCreated || 0,
                          waterWash: waterWashCount
                        })
                      }
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.15)";
                        e.currentTarget.style.boxShadow = isWaterWashOnly
                          ? "0 4px 12px rgba(255, 140, 66, 0.6)"
                          : isAboveAverage
                          ? "0 4px 12px rgba(0, 158, 247, 0.5)"
                          : "0 4px 12px rgba(80, 205, 137, 0.4)";
                        e.currentTarget.style.zIndex = "10";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.zIndex = "1";
                      }}
                    >
                      {count > 0 ? (
                        <span
                          className="fw-bold"
                          style={{
                            fontSize: "9px",
                            color:
                              intensity >= 3
                                ? "#ffffff"
                                : isDarkMode
                                ? "#a1a5b7"
                                : "#3f4254",
                            lineHeight: 1,
                            textShadow:
                              intensity >= 3
                                ? "0 1px 2px rgba(0,0,0,0.3)"
                                : "none",
                          }}
                        >
                          {count}
                        </span>
                      ) : isWaterWashOnly ? (
                        <span
                          className="fw-bold"
                          style={{
                            fontSize: "9px",
                            color: "#ffffff",
                            lineHeight: 1,
                            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                          }}
                        >
                          {waterWashCount}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="d-flex flex-wrap gap-3 mt-4 justify-content-center">
          {/* Green legend - At/Below Average */}
          <div className="d-flex align-items-center gap-2">
            <span
              className={`fs-9 ${isDarkMode ? "text-white" : "text-muted"}`}
              style={{ fontSize: "9px", opacity: 0.7 }}
            >
              At/Below Avg
            </span>
            {[1, 2, 3, 4].map((intensity) => (
              <div
                key={intensity}
                className="rounded"
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: getColor(averageCount, intensity),
                  border: `1px solid ${isDarkMode ? "#2d2d3f" : "#e4e6ef"}`,
                }}
              />
            ))}
          </div>
          {/* Blue legend - Above Average */}
          <div className="d-flex align-items-center gap-2">
            <span
              className={`fs-9 ${isDarkMode ? "text-white" : "text-muted"}`}
              style={{ fontSize: "9px", opacity: 0.7 }}
            >
              Above Avg
            </span>
            {[1, 2, 3, 4].map((intensity) => (
              <div
                key={intensity}
                className="rounded"
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: getColor(averageCount + 1, intensity),
                  border: `1px solid ${isDarkMode ? "#2d2d3f" : "#e4e6ef"}`,
                }}
              />
            ))}
          </div>
          {/* Average indicator */}
          <div>
            <span
              className={`fs-9 ${isDarkMode ? "text-white" : "text-muted"}`}
              style={{ fontSize: "9px", opacity: 0.6 }}
            >
              Avg: {averageCount.toFixed(1)} jobs/day
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Get month name for header
  const now = new Date();
  const monthName = now.toLocaleString('en-US', { month: 'long' });

  return (
    <div
      className={`card ${className || ''}`}
      style={{ backgroundColor: isDarkMode ? "#1e1e2d" : "#ffffff" }}
    >
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span
            className={`card-label fw-bolder fs-3 ${
              isDarkMode ? "text-white" : "text-dark"
            }`}
          >
            <i
              className="bi bi-calendar-week me-2"
              style={{ color: "#50cd89" }}
            ></i>
            Activity Heat Map
          </span>
          <span className="text-muted mt-1 fw-bold fs-7">
            {monthName} job completion activity
          </span>
        </h3>
        <div className="card-toolbar">
          <div className="d-flex gap-3">
            <div className="d-flex align-items-center">
              <i
                className="bi bi-check-circle-fill me-2"
                style={{ color: "#50cd89" }}
              ></i>
              <div>
                <div
                  className={`fs-7 ${isDarkMode ? "text-white" : "text-muted"}`}
                  style={{ opacity: 0.7 }}
                >
                  Total Jobs
                </div>
                <div
                  className={`fw-bold ${
                    isDarkMode ? "text-white" : "text-dark"
                  }`}
                >
                  {heatmapData.reduce((sum, d) => sum + d.count, 0)}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <i
                className="bi bi-graph-up me-2"
                style={{ color: "#009ef7" }}
              ></i>
              <div>
                <div
                  className={`fs-7 ${isDarkMode ? "text-white" : "text-muted"}`}
                  style={{ opacity: 0.7 }}
                >
                  Daily Avg
                </div>
                <div
                  className={`fw-bold ${
                    isDarkMode ? "text-white" : "text-dark"
                  }`}
                >
                  {(() => {
                    const daysWithJobs = heatmapData.filter((d) => d.count > 0)
                      .length;
                    return daysWithJobs > 0
                      ? (
                          heatmapData.reduce((sum, d) => sum + d.count, 0) /
                          daysWithJobs
                        ).toFixed(1)
                      : "0.0";
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body pt-3 pb-5">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <span className="spinner-border text-primary"></span>
          </div>
        ) : (
          <HeatmapCalendar />
        )}
      </div>

      {/* Day Details Modal */}
      {selectedDay && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.7)" }}
          onClick={() => setSelectedDay(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: isDarkMode ? "#1e1e2d" : "#ffffff",
                border: isDarkMode ? "1px solid #2d2d3f" : "1px solid #e4e6ef",
              }}
            >
              <div
                className="modal-header border-0 pb-0"
                style={{
                  borderBottom: isDarkMode
                    ? "1px solid #2d2d3f"
                    : "1px solid #e4e6ef",
                }}
              >
                <h5
                  className={`modal-title fw-bolder ${
                    isDarkMode ? "text-white" : "text-dark"
                  }`}
                >
                  <i
                    className="bi bi-calendar-day me-2"
                    style={{
                      color:
                        selectedDay.jobsClosed > 0 ? "#50cd89" : "#a1a5b7",
                    }}
                  ></i>
                  {selectedDay.date.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </h5>
                <button
                  type="button"
                  className={`btn-close ${isDarkMode ? "btn-close-white" : ""}`}
                  onClick={() => setSelectedDay(null)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body py-5">
                {selectedDay.jobsClosed > 0 || selectedDay.jobsCreated > 0 || selectedDay.waterWash > 0 ? (
                  <div className="row g-4">
                    {/* Jobs Closed */}
                    <div className="col-6">
                      <div
                        className="text-center p-4 rounded-3"
                        style={{
                          backgroundColor: isDarkMode ? "#1c1c28" : "#f5f8fa",
                          border: `2px solid ${
                            isDarkMode ? "#2d2d3f" : "#e4e6ef"
                          }`,
                        }}
                      >
                        <i
                          className="bi bi-check-circle-fill fs-2x mb-3"
                          style={{ color: "#50cd89", display: "block" }}
                        ></i>
                        <div
                          className={`fs-2x fw-bolder ${
                            isDarkMode ? "text-white" : "text-dark"
                          } mb-2`}
                        >
                          {selectedDay.jobsClosed}
                        </div>
                        <div className="text-muted fs-7 fw-bold text-uppercase">
                          Jobs Closed
                        </div>
                      </div>
                    </div>

                    {/* Jobs Created */}
                    <div className="col-6">
                      <div
                        className="text-center p-4 rounded-3"
                        style={{
                          backgroundColor: isDarkMode ? "#1c1c28" : "#f5f8fa",
                          border: `2px solid ${
                            isDarkMode ? "#2d2d3f" : "#e4e6ef"
                          }`,
                        }}
                      >
                        <i
                          className="bi bi-plus-circle-fill fs-2x mb-3"
                          style={{ color: "#7239ea", display: "block" }}
                        ></i>
                        <div
                          className={`fs-2x fw-bolder ${
                            isDarkMode ? "text-white" : "text-dark"
                          } mb-2`}
                        >
                          {selectedDay.jobsCreated}
                        </div>
                        <div className="text-muted fs-7 fw-bold text-uppercase">
                          Jobs Created
                        </div>
                      </div>
                    </div>

                    {/* Water Wash */}
                    <div className="col-6">
                      <div
                        className="text-center p-4 rounded-3"
                        style={{
                          backgroundColor: isDarkMode ? "#1c1c28" : "#f5f8fa",
                          border: `2px solid ${
                            isDarkMode ? "#2d2d3f" : "#e4e6ef"
                          }`,
                        }}
                      >
                        <i
                          className="bi bi-droplet-fill fs-2x mb-3"
                          style={{ color: "#009ef7", display: "block" }}
                        ></i>
                        <div
                          className={`fs-2x fw-bolder ${
                            isDarkMode ? "text-white" : "text-dark"
                          } mb-2`}
                        >
                          {selectedDay.waterWash}
                        </div>
                        <div className="text-muted fs-7 fw-bold text-uppercase">
                          Water Wash
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <i
                      className="bi bi-inbox fs-3x text-muted mb-3"
                      style={{ display: "block" }}
                    ></i>
                    <div
                      className={`${
                        isDarkMode ? "text-gray-400" : "text-muted"
                      }`}
                    >
                      No activity on this day
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-light-primary btn-sm"
                  onClick={() => setSelectedDay(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
